import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Button, Hidden, Link, Menu, MenuItem, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { makeStyles } from '@mui/styles';
import { DataGridPremium as DataGrid, GridColDef } from '@mui/x-data-grid-premium';
import { Document_FieldsFragment, useDocument_DownloadFilesMutation } from 'queries';

import DocumentPreviewModal from 'pages/common/document/document-preview-modal';
import SignatureTypePill from 'pages/common/document/signature-type-pill';
import InstructionsModal from 'pages/estate-vault/components/instructions-modal';
import { NodeEnvironment } from 'utils/enum';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  container: {
    marginLeft: '20px',
  },
  emptyContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    marginTop: '8px',
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    marginTop: '24px',
  },
  subtitle: {
    fontSize: '14px',
    marginTop: '12px',
  },
  dataGrid: {
    border: 0,
    borderRadius: '8px',
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
    '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader': {
      display: 'none',
    },
    '& .MuiDataGrid-virtualScroller': {
      marginTop: '0 !important',
    },
    '& .MuiDataGrid-row': {
      borderBottom: '1px solid #ECEEF1',
      borderTop: 'none',
    },
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
      borderTop: 'none',
      paddingLeft: '0px',
      paddingRight: '0px',
    },
  },
  documentTitle: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  documentSubtitle: {
    fontSize: '14px',
    marginRight: '8px',
  },
  downloadButton: {
    cursor: 'pointer',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '700',
  },
  button: {
    backgroundColor: '#1C2541',
    color: '#FFFFFF',
    borderRadius: '24px',
    boxShadow: 'none',
    fontSize: '12px',
    px: '16px',
    width: '120px',
  },
  link: {
    cursor: 'pointer',
  },
}));

type Props = {
  documents: Document_FieldsFragment[];
  onRefreshDocuments: () => void;
};

const AssignedGeneratedDocuments = ({ documents, onRefreshDocuments }: Props) => {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();
  const [selectedPreviewDocument, setSelectedPreviewDocument] = useState<Document_FieldsFragment>();

  const [selectedMenuDocument, setSelectedMenuDocument] = useState<Document_FieldsFragment>();
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const [downloadFile] = useDocument_DownloadFilesMutation({
    onCompleted: ({ data: downloadFilesData }) => {
      window.open(downloadFilesData[0].signedUrl, '_blank');
    },
    onError: (e) => {
      showAlert({
        message: 'Error downloading files: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    document: Document_FieldsFragment,
  ) => {
    setSelectedMenuDocument(document);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setSelectedMenuDocument(undefined);
    setMenuAnchorEl(null);
  };

  const handleMenuInstructionsClicked = () => {
    setShowInstructionsModal(true);
    setMenuAnchorEl(null);
  };

  const getTableColumns = useCallback(() => {
    const columns: GridColDef[] = [
      {
        flex: 2,
        field: 'name',
        renderCell: (params) => {
          return (
            <Box
              className={classes.documentTitle}
              onClick={() => setSelectedPreviewDocument(params.row)}
            >
              <Typography className={classes.documentSubtitle}>{params.value}</Typography>
            </Box>
          );
        },
      },
      {
        flex: 2,
        field: 'signatureType',
        renderCell: (params) => <SignatureTypePill signatureType={params.value} />,
      },
      {
        flex: 1,
        field: 'edit',
        renderCell: (params) =>
          process.env.REACT_APP_NODE_ENV !== NodeEnvironment.PROD && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                width: '100%',
                justifyContent: 'center',
              }}
            >
              <div>
                <Link
                  href={`/home/${portalId}/task/${params.row.estateWorkItemId}/template-data-wizard`}
                >
                  <Button
                    component="span"
                    className={classes.button}
                    color="primary"
                    variant="contained"
                    size="small"
                  >
                    <Hidden mdDown>
                      <Typography>Edit</Typography>
                    </Hidden>
                  </Button>
                </Link>
              </div>
            </div>
          ),
      },
      {
        flex: 0.1,
        field: 'download',
        renderCell: (params) => (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              justifyContent: 'center',
            }}
          >
            <DownloadIcon
              className={classes.downloadButton}
              fontSize="small"
              onClick={() => {
                downloadFile({
                  variables: {
                    portalId,
                    ids: [params.row.id],
                  },
                });
              }}
            />
          </div>
        ),
      },
      {
        flex: 0.4,
        field: 'actions',
        renderCell: (params) => (
          <IconButton
            aria-label="more"
            id="long-button"
            aria-controls={menuOpen ? 'long-menu' : undefined}
            aria-expanded={menuOpen ? 'true' : undefined}
            aria-haspopup="true"
            onClick={(event) => handleMenuOpen(event, params.row)}
          >
            <MoreVertIcon />
          </IconButton>
        ),
      },
    ];

    return columns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRefreshDocuments, classes]);

  return (
    <>
      {selectedMenuDocument && (
        <InstructionsModal
          showModal={showInstructionsModal}
          setShowModal={() => {
            setShowInstructionsModal(false);
          }}
          documentGenerationTemplate={selectedMenuDocument.documentGenerationTemplate || undefined}
        />
      )}
      <Box className={classes.container}>
        <Typography className={classes.sectionTitle}>Generated Documents</Typography>
        <DataGrid
          className={classes.dataGrid}
          columns={getTableColumns()}
          rows={documents}
          disableColumnReorder
          disableRowSelectionOnClick
          disableColumnMenu
          hideFooter
          columnHeaderHeight={0}
          autoHeight={true}
        />
        {selectedPreviewDocument && (
          <DocumentPreviewModal
            setShowModal={() => setSelectedPreviewDocument(undefined)}
            showModal={true}
            document={selectedPreviewDocument}
          />
        )}
      </Box>
      <Menu
        id="long-menu"
        MenuListProps={{
          'aria-labelledby': 'long-button',
        }}
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
      >
        <MenuItem key="instructions" onClick={() => handleMenuInstructionsClicked()}>
          Instructions
        </MenuItem>
      </Menu>
    </>
  );
};

export default AssignedGeneratedDocuments;
